import { useCallback, useMemo } from 'react';
import { formatISO, setDate, sub } from 'date-fns';
import { GridColDef, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';

import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { formatPhoneNumber, getDateByYearsMonths, getDiffYearsMonths, gridDateFormatter } from '@shared/commonFunctions';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColPhone } from '@shared/components/grid/GridColPhone';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { gridBooleanCell, gridCurrencyCell, gridStateCell, gridZipCell, dataGridCol } from '@shared/components/grid/common';
import { IMap } from '@shared/interfaces';
import { EMPLOYMENT_TYPE_OPTIONS } from '@shared/lists';
import { GridColDate } from '@shared/components/grid/GridColDate';
import { SelectDateEventType } from '@shared/components/SelectDate';

import { ContactSection } from '../ContactSection';
import { contactEmploymentValidationSchema } from './validation';

interface IEmploymentProps {
	data?: NContact.Employment[];
  mutateCombinedDti?: () => void;
	onChange(data: NContact.Employment[]): Promise<IMap<string> | undefined>;
}

const handleDateChange = (
  event: SelectDateEventType,
  params: GridRenderEditCellParams,
  api: React.MutableRefObject<GridApiPro>
) => {
  const { id } = params;
  const value = event.target.value;
  const years = value ? getDiffYearsMonths(value)[0] : params.row.yrs;
  const mths = value ? getDiffYearsMonths(value)[1] : params.row.mths;
  api.current.setEditCellValue({ id , field: 'yrs', value: years });
  api.current.setEditCellValue({ id , field: 'mths', value: mths });
};

const handleYrsMthsChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  params: GridRenderEditCellParams,
  api: React.MutableRefObject<GridApiPro>,
  isYearChange: boolean
) => {
  const { id } = params;
  const value = Number(event.target.value);
  const years = isYearChange ? value : params.row.yrs;
  const mths = !isYearChange ? value : params.row.mths;

  if (typeof years === 'number' || typeof mths === 'number') {
    api.current.setEditCellValue({ id, field: 'startDate', value: getDateByYearsMonths(years, mths, true)});
  }
};

const columns: GridColDef<NContact.Employment>[] = [
  dataGridCol('isCurrent', {
    headerName: 'Current',
    minWidth: 55,
    flex: 0.4,
    ...gridBooleanCell,
  }),
  dataGridCol('isPrimary', {
    headerName: 'Primary',
    minWidth: 50,
    flex: 0.4,
    ...gridBooleanCell,
  }),
  dataGridCol('companyName', {
    headerName: 'Company',
    minWidth: 80,
    flex: 1.8,
    textWrap: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('jobTitle', {
    headerName: 'Position',
    minWidth: 80,
    flex: 1.5,
    textWrap: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('employmentType', {
    headerName: 'Type',
    minWidth: 80,
    flex: 0.8,
    textWrap: true,
    renderCell: (params) => <div>{EMPLOYMENT_TYPE_OPTIONS.find((type) => type.value === params.value)?.label}</div>,
    renderEditCell: (params) => (
      <GridColSelect
        isSearchable
        canBeClean={false}
        options={EMPLOYMENT_TYPE_OPTIONS}
        params={params}
      />
    ),
  }),
  dataGridCol('monthlyIncomeAmount', {
    headerName: 'Monthly Income',
    minWidth: 80,
    flex: 0.7,
    ...gridCurrencyCell,
    textWrap: true,
  }),
  dataGridCol('startDate', {
    type: 'date',
    minWidth: 96,
    flex: 1.2,
    textWrap: true,
    renderEditCell: (params) => {
      const api = useGridApiContext();
      const updatedParams = {
        ...params,
        value: params.row.isCurrent ? params.value : null
      };

      return (
        <GridColDate
          params={updatedParams}
          disabled={!params.row.isCurrent}
          onChange={(event: SelectDateEventType) => handleDateChange(event, updatedParams, api)}
        />
      );
    },
    valueFormatter: (params) => gridDateFormatter(params, true),
  }),
  dataGridCol('yrs', {
    minWidth: 40,
    flex: 0.4,
    renderCell: (params) => Math.floor((params.row.monthsAtJob || 0) / 12),
    renderEditCell: (params) => {
      const api = useGridApiContext();
      const startDateFocus = api.current.getCellParams(params.id, 'startDate').hasFocus;

      return (
      <GridColNumber
        params={params}
        limit={2}
        onChange={(event) => !startDateFocus && handleYrsMthsChange(event, params, api, true)}
      />
    );
  }
  }),
  dataGridCol('mths', {
    minWidth: 35,
    flex: 0.4,
    renderCell: (params) => Math.floor((params.row.monthsAtJob || 0) % 12),
    renderEditCell: (params) => {
      const api = useGridApiContext();
      const startDateFocus = api.current.getCellParams(params.id, 'startDate').hasFocus;

      return (
      <GridColNumber
        params={params}
        limit={2}
        onChange={(event) => !startDateFocus && handleYrsMthsChange(event, params, api, false)}
      />
    );
  }
  }),
  dataGridCol('phone', {
    minWidth: 90,
    flex: 1.3,
    textWrap: true,
    valueFormatter: ({ value }) => formatPhoneNumber(value),
    renderEditCell: (params) => <GridColPhone params={params} />,
  }),
  dataGridCol('streetLine1', {
    headerName: 'Street',
    minWidth: 90,
    flex: 1.5,
    textWrap: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('unit', {
    minWidth: 90,
    flex: 1.4,
    textWrap: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('city', {
    minWidth: 85,
    flex: 1.2,
    textWrap: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('state', {
    ...gridStateCell,
    minWidth: 50,
    flex: 0.7,
  }),
  dataGridCol('zip', {
    ...gridZipCell,
    minWidth: 70,
    flex: 1,
    textWrap: true,
  }),
  dataGridCol('monthsAtJob', {
    hide: true,
    minWidth: 70,
    flex: 0.7,
    valueSetter: ({ row }) => ({
      ...row,
      monthsAtJob: (+row.yrs || 0) * 12 + (+row.mths || 0),
    }),
  }),
].map((i) => ({ ...i, editable: true, sortable: false, disableColumnMenu: true }));

export const Employment = ({ data = [], mutateCombinedDti, onChange}: IEmploymentProps) => {
  const handleOnChange = useCallback(
    (values: NContact.Employment[]) => {
      const defaultIsCurrent = data.length === 0;

      const getStartDate = (row: NContact.Employment) => {
        let startdate = row.startDate;
        
        if (!startdate && (defaultIsCurrent || row.isCurrent && (typeof row.yrs === 'number' || typeof row.mths === 'number'))) {
          startdate = formatISO(sub(setDate(new Date(), 1), { years: row.yrs as number, months: row.mths as number }));
        }
        
        return startdate;
      };
      
      const updatedData = values.map(row => ({
        ...row,
        startDate: defaultIsCurrent || row.isCurrent ? getStartDate(row) : null,
        yrs: row.yrs ? row.yrs : null,
        mths: row.mths ? row.mths : null,
        isPrimary: defaultIsCurrent ? defaultIsCurrent : row.isPrimary,
        isCurrent: defaultIsCurrent ? defaultIsCurrent : row.isCurrent,
      }));

      onChange(updatedData);
    },
    [data]
  );

	const rows = useMemo(
		() => data.map((row) => ({
      ...row,
      yrs: Math.floor((row.monthsAtJob || 0) / 12),
      mths: Math.floor((row.monthsAtJob || 0) % 12)
    })).sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent)),
		[data]
	);

	return (
		<ContactSection
			strict
			name="Employment"
			buttonTitle="+ New"
			data={rows}
			columns={columns}
			validateSchema={contactEmploymentValidationSchema}
      mutateCombinedDti={mutateCombinedDti}
			onChange={handleOnChange}
		/>
	);
};
